import { useState, FormEvent } from 'react';

export default function FinalCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d7ftupvimhg6tri2vldg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-0 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/7785f4c0f1f73948db776e45d97ce24a.jpg"
          alt="Protect your property with PRA Roof Contractors"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/95 via-[#0d0d0d]/80 to-[#0d0d0d]/70"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left Text */}
          <div className="lg:w-1/2">
            <span className="text-[#c0392b] text-xs font-bold tracking-widest uppercase mb-4 block">Get Started Today</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Protect Your Property<br />
              <span className="text-[#c0392b]">Before the Next</span><br />
              Storm Hits
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-8 max-w-md">
              Get a professional inspection and expert guidance from a multi-state roofing and restoration team trusted since 1998.
            </p>

            {/* Contact Info */}
            <div className="flex flex-col gap-4">
              <a href="tel:2813779180" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-[#c0392b]/20 rounded-full">
                  <i className="ri-phone-line text-[#c0392b]"></i>
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-widest">Call Us</p>
                  <p className="font-bold text-sm">(281) 377-9180</p>
                </div>
              </a>
              <a href="mailto:info@praroof.com" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-[#c0392b]/20 rounded-full">
                  <i className="ri-mail-line text-[#c0392b]"></i>
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-widest">Email Us</p>
                  <p className="font-bold text-sm">info@praroof.com</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:w-1/2 w-full">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#c0392b]/20 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-[#c0392b] text-3xl"></i>
                  </div>
                  <h3 className="text-white font-black text-xl mb-2">Request Received!</h3>
                  <p className="text-white/60 text-sm">Our team will contact you within 24 hours to schedule your free inspection.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-white font-black text-xl mb-1">Schedule Your Free Inspection</h3>
                  <p className="text-white/50 text-xs mb-6">No obligation. Fast response. Expert assessment.</p>

                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/60 text-xs mb-1.5 block">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="John Smith"
                          className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#c0392b] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="text-white/60 text-xs mb-1.5 block">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="(281) 555-0000"
                          className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#c0392b] transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-white/60 text-xs mb-1.5 block">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@example.com"
                        className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#c0392b] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="text-white/60 text-xs mb-1.5 block">Property Location *</label>
                      <input
                        type="text"
                        name="property_location"
                        required
                        placeholder="Houston, TX"
                        className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#c0392b] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="text-white/60 text-xs mb-1.5 block">Service Needed</label>
                      <select
                        name="service_needed"
                        className="w-full bg-white/10 border border-white/15 text-white text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#c0392b] transition-colors cursor-pointer appearance-none"
                      >
                        <option value="" className="bg-[#1a1a1a]">Select a service...</option>
                        <option value="Roof Replacement" className="bg-[#1a1a1a]">Roof Replacement</option>
                        <option value="Roof Repair" className="bg-[#1a1a1a]">Roof Repair</option>
                        <option value="Storm Damage Restoration" className="bg-[#1a1a1a]">Storm Damage Restoration</option>
                        <option value="Roof Installation" className="bg-[#1a1a1a]">Roof Installation</option>
                        <option value="Siding" className="bg-[#1a1a1a]">Siding</option>
                        <option value="Gutters" className="bg-[#1a1a1a]">Gutters</option>
                        <option value="Free Inspection" className="bg-[#1a1a1a]">Free Inspection</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-white/60 text-xs mb-1.5 block">Message</label>
                      <textarea
                        name="message"
                        rows={3}
                        maxLength={500}
                        placeholder="Describe your roofing needs or any storm damage..."
                        className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#c0392b] transition-colors resize-none"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#c0392b] hover:bg-[#a93226] disabled:opacity-60 text-white font-bold py-4 rounded-full text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? 'Sending...' : 'Schedule Your Free Inspection'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Call Button */}
      <a
        href="tel:2813779180"
        className="fixed bottom-6 right-6 z-50 md:hidden flex items-center gap-2 bg-[#c0392b] text-white font-bold px-5 py-3.5 rounded-full shadow-lg cursor-pointer whitespace-nowrap"
      >
        <i className="ri-phone-fill text-base"></i>
        Call Now
      </a>
    </section>
  );
}
